import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button as MuiButton,
  Chip,
  Container,
  Grid,
  InputAdornment,
  OutlinedInput,
  Paper,
  Typography,
} from '@mui/material';
import {
  Briefcase,
  Calendar,
  Clock,
  Copy,
  Link as LinkIcon,
  Mail,
  MapPin,
  Pause,
  Play,
} from 'lucide-react';
import { toast } from '../../../../../../ui/toast';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../i18n/constants/keys';
import LogoWhite from '../../../../../../assets/images/logos/white-bg-logo-hiacuity.png';
import { Button } from '../../../../../../ui';
import { ensureValidDate, formatDate } from '../../../../../../utils/helpers/dateUtils';
import {
  IJobPostingPreview,
  useJobPostingPreview,
} from './Hooks/UseJobPostingPreview';

export const JobPostingPreview: React.FC<IJobPostingPreview> = (props) => {
  const { jobPostingData, jobPostId, hidePublishBanner } = props;
  const { t } = useTranslation(NS.adminPages);
  const {
    createPost,
    url,
    videoRef,
    handlePlayPause,
    isPlaying,
    companyLogo,
    isCreatingPost,
    previewHtml,
  } = useJobPostingPreview(props);

  const formatDateString = (dateValue: any): string => {
    const formatted = formatDate(dateValue, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    return formatted || 'TBD';
  };

  const calculateDaysLeft = (): string => {
    const endDate = ensureValidDate(jobPostingData.endDate);
    if (!endDate) return 'TBD';

    const currentDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    currentDate.setHours(0, 0, 0, 0);

    const timeDiff = endDate.getTime() - currentDate.getTime();
    const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24));

    if (daysDiff < 0) {
      return t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.expired);
    } else if (daysDiff === 0) {
      return t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.expiresToday);
    } else if (daysDiff === 1) {
      return t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.oneDayLeft);
    } else {
      return t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.daysLeftCount, { count: daysDiff });
    }
  };

  const keyInfo = [
    {
      key: 'department',
      label: t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.department),
      value: jobPostingData.department,
      icon: Briefcase,
      color: '#2563eb',
      bgColor: '#dbeafe',
    },
    {
      key: 'location',
      label: t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.location),
      value: jobPostingData.location,
      icon: MapPin,
      color: '#059669',
      bgColor: '#d1fae5',
    },
    {
      key: 'workType',
      label: t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.workType),
      value: jobPostingData.workType,
      icon: Briefcase,
      color: '#9333ea',
      bgColor: '#f3e8ff',
    },
    {
      key: 'timeline',
      label: t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.daysLeftLabel),
      value: `${calculateDaysLeft()} - ${formatDateString(
        jobPostingData.startDate ?? '',
      )} - ${formatDateString(jobPostingData.endDate ?? '')}`,
      icon: Calendar,
      color: '#d97706',
      bgColor: '#fef3c7',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Job Header */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          py: 2.5,
          borderRadius: 0,
        }}
      >
        <Container maxWidth='lg'>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
              <Paper
                variant='outlined'
                sx={{
                  display: 'flex',
                  height: 52,
                  minWidth: 52,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 2,
                  p: 1,
                  bgcolor: 'background.paper',
                }}
              >
                <Box
                  component='img'
                  src={companyLogo || LogoWhite}
                  alt='Company Logo'
                  sx={{ maxHeight: 36, maxWidth: 140, objectFit: 'contain' }}
                />
              </Paper>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
                  <Chip
                    label={t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.badge)}
                    size='small'
                    color='success'
                    variant='outlined'
                    sx={{ fontWeight: 700, fontSize: '0.7rem', height: 22 }}
                  />
                </Box>
                <Typography
                  variant='h6'
                  noWrap
                  sx={{ fontWeight: 800, color: 'text.primary', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                >
                  {jobPostingData.jobTitle}
                </Typography>
              </Box>
            </Box>

            {/* Compact Deadline Info */}
            <Paper
              variant='outlined'
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 2,
                bgcolor: 'success.light',
                borderColor: 'success.main',
                color: 'success.dark',
                width: 'fit-content',
              }}
            >
              <Clock size={16} />
              <Typography variant='body2' sx={{ fontWeight: 700 }}>
                {calculateDaysLeft()}
                <Typography component='span' variant='body2' sx={{ ml: 0.5, fontWeight: 500 }}>
                  {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.toApply)}
                </Typography>
              </Typography>
            </Paper>
          </Box>
        </Container>
      </Paper>

      <Container maxWidth='lg' sx={{ py: 3 }}>
        <Grid container spacing={3}>
          {/* Main Content */}
          <Grid item xs={12} lg={8}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Job Info Cards */}
              <Grid container spacing={2}>
                {keyInfo.map((item) => (
                  <Grid item xs={12} sm={6} key={item.key}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            width: 40,
                            height: 40,
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 2,
                            bgcolor: item.bgColor,
                            color: item.color,
                          }}
                        >
                          <item.icon size={20} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            {item.label}
                          </Typography>
                          <Typography
                            variant='body2'
                            noWrap
                            sx={{ fontWeight: 700, color: 'text.primary' }}
                          >
                            {item.value}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* Job Description */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Typography variant='h6' sx={{ mb: 2, fontWeight: 800, color: 'text.primary' }}>
                  {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.jobDescription)}
                </Typography>
                <Box
                  sx={{
                    color: 'text.primary',
                    '& h1': { my: 2, fontSize: '1.25rem', fontWeight: 800 },
                    '& h2': { my: 1.5, fontSize: '1.1rem', fontWeight: 700 },
                    '& h3': { my: 1.5, fontSize: '1rem', fontWeight: 700 },
                    '& p': { mb: 1.5, lineHeight: 1.6 },
                    '& ul': { mb: 1.5, pl: 3 },
                    '& li': { mb: 0.5, lineHeight: 1.6 },
                    '& strong': { fontWeight: 700 },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: previewHtml,
                  }}
                />
              </Paper>
            </Box>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} lg={4}>
            <Box sx={{ position: { lg: 'sticky' }, top: { lg: 24 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Video and Company Card */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                {url && (
                  <Box sx={{ mb: 3 }}>
                    {jobPostingData.videoTitle && (
                      <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 0.5 }}>
                        {jobPostingData.videoTitle}
                      </Typography>
                    )}
                    {jobPostingData.videoDescription && (
                      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
                        {jobPostingData.videoDescription}
                      </Typography>
                    )}
                    <Paper
                      variant='outlined'
                      sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', mb: 2 }}
                    >
                      <Box
                        component='video'
                        ref={videoRef}
                        controls
                        poster='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTgwIiBmaWxsPSIjRkJGREZGIi8+CjxwYXRoIGQ9Ik0xNDUgNzJMMTc1IDkwTDE0NSAxMDhWNzJaIiBmaWxsPSIjMDU5NjY5Ii8+Cjx0ZXh0IHg9IjE2MCIgeT0iMTMwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM2QjczODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiPlZpZGVvIFByZXZpZXc8L3RleHQ+Cjwvc3ZnPg=='
                        sx={{ width: '100%', borderRadius: 1.5, mb: 1.5, boxShadow: 1 }}
                      >
                        <source src={url} type='video/mp4' />
                        {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.browserNotSupported)}
                      </Box>
                      <MuiButton
                        onClick={handlePlayPause}
                        fullWidth
                        variant='contained'
                        color='primary'
                        startIcon={isPlaying ? <Pause size={16} /> : <Play size={16} />}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                      >
                        {isPlaying
                          ? t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.pause)
                          : t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.play)}
                      </MuiButton>
                    </Paper>
                  </Box>
                )}

                {/* Apply Section */}
                <Paper
                  elevation={0}
                  sx={{
                    mb: 3,
                    p: 2.5,
                    borderRadius: 2.5,
                    textAlign: 'center',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                  }}
                >
                  <Typography variant='h6' sx={{ fontWeight: 800, mb: 0.5, color: 'inherit' }}>
                    {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.readyToApply)}
                  </Typography>
                  <Typography variant='body2' sx={{ mb: 2, opacity: 0.9, color: 'inherit' }}>
                    {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.joinTeam)}
                  </Typography>
                  <MuiButton
                    onClick={() => {}}
                    fullWidth
                    variant='contained'
                    sx={{
                      bgcolor: 'background.paper',
                      color: 'primary.main',
                      fontWeight: 700,
                      py: 1.2,
                      '&:hover': { bgcolor: 'background.default' },
                    }}
                  >
                    {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.applyNow)}
                  </MuiButton>
                </Paper>

                {/* Share Section */}
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 0.5 }}>
                    {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.shareOpportunity)}
                  </Typography>
                  <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
                    {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.helpFindCandidate)}
                  </Typography>

                  {jobPostId && (
                    <Box sx={{ mb: 2 }}>
                      <OutlinedInput
                        fullWidth
                        size='small'
                        readOnly
                        value={`${window.location.origin}/jobs/${jobPostId}`}
                        startAdornment={
                          <InputAdornment position='start'>
                            <LinkIcon size={16} color='#059669' />
                          </InputAdornment>
                        }
                        sx={{
                          fontSize: '0.8rem',
                          fontFamily: 'monospace',
                          bgcolor: 'background.default',
                          borderRadius: 2,
                        }}
                      />
                    </Box>
                  )}

                  {/* Action Buttons */}
                  <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                    <Grid item xs={6}>
                      <MuiButton
                        fullWidth
                        variant='outlined'
                        size='small'
                        startIcon={<Copy size={14} />}
                        onClick={() => {
                          navigator.clipboard.writeText(
                            `${window.location.origin}/jobs/${jobPostId}`,
                          );
                          toast.success(t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.urlCopied), {
                            position: 'bottom-center',
                          });
                        }}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                      >
                        {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.copyUrl)}
                      </MuiButton>
                    </Grid>
                    <Grid item xs={6}>
                      <MuiButton
                        fullWidth
                        variant='contained'
                        size='small'
                        startIcon={<Mail size={14} />}
                        onClick={() => {}}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                      >
                        {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.emailFriend)}
                      </MuiButton>
                    </Grid>
                  </Grid>

                  {/* Social Media Sharing */}
                  <Box sx={{ pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography
                      variant='caption'
                      sx={{ display: 'block', mb: 1.5, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'text.secondary' }}
                    >
                      {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.socialNetworks)}
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <MuiButton
                          fullWidth
                          variant='outlined'
                          size='small'
                          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: 'divider' }}
                        >
                          LinkedIn
                        </MuiButton>
                      </Grid>
                      <Grid item xs={6}>
                        <MuiButton
                          fullWidth
                          variant='outlined'
                          size='small'
                          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: 'divider' }}
                        >
                          WhatsApp
                        </MuiButton>
                      </Grid>
                      <Grid item xs={6}>
                        <MuiButton
                          fullWidth
                          variant='outlined'
                          size='small'
                          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: 'divider' }}
                        >
                          Facebook
                        </MuiButton>
                      </Grid>
                      <Grid item xs={6}>
                        <MuiButton
                          fullWidth
                          variant='outlined'
                          size='small'
                          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: 'divider' }}
                        >
                          X
                        </MuiButton>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {!hidePublishBanner && (
        <Container maxWidth='lg' sx={{ pb: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'success.light',
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant='subtitle1' sx={{ fontWeight: 800, color: 'text.primary' }}>
                {jobPostId
                  ? t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.updateJobPost)
                  : t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.publishJob)}
              </Typography>
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                {jobPostId
                  ? t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.updateSubtitle)
                  : t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.publishSubtitle)}
              </Typography>
            </Box>
            <Button
              onClick={createPost}
              variant='primary'
              label={
                isCreatingPost
                  ? jobPostId
                    ? t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.updating)
                    : t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.creating)
                  : jobPostId
                    ? t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.updateButton)
                    : t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preview.createButton)
              }
              sx={{ width: { xs: '100%', sm: 'auto' } }}
              disabled={
                isCreatingPost ||
                !jobPostingData.jobTitle ||
                !jobPostingData.department ||
                !jobPostingData.location
              }
            />
          </Paper>
        </Container>
      )}
    </Box>
  );
};

export default JobPostingPreview;
